<template>
  <div>
    <b-table
      small
      responsive
      bordered
      :fields="fields"
      :items="rows"
      class="mb-2"
    >
      <template #cell(tcLabel)="{ item }">
        TC{{ item.tcLabel }}
      </template>

      <template #cell(existingKeywordName)="{ item }">
        {{ item.existingKeywordName || '—' }}
      </template>

      <template #cell(tcAction)="{ item }">
        <b-form-select
          v-if="item.existingKeywordId"
          v-model="item.tcAction"
          :options="existingActionOptions"
          size="sm"
          style="min-width: 8rem;"
          @change="onActionChange(item)"
        />
        <span v-else class="text-muted">Add</span>
      </template>

      <template #cell(tcProposedName)="{ item }">
        <b-form-input
          v-if="item.tcAction !== 'remove'"
          v-model="item.tcProposedName"
          size="sm"
          style="min-width: 18rem;"
          @input="onProposedNameInput(item)"
        />
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(similar)="{ item }">
        <b-button
          v-if="isDraftRow(item)"
          size="sm"
          variant="link"
          class="px-1 text-nowrap text-danger"
          @click="removeDraftRow(item)"
        >
          Remove
        </b-button>
        <b-button
          size="sm"
          variant="link"
          class="px-1 text-nowrap"
          :disabled="!item.tcProposedName || similarBusy"
          @click="$emit('see-similar', item)"
        >
          See similar keywords
        </b-button>
      </template>
    </b-table>

    <b-button size="sm" variant="outline-primary" @click="addRow">
      Add keyword
    </b-button>
  </div>
</template>

<script>
import Revisions from '@/services/Api/TechnicalAreas/Revisions';

function draftId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default {
  name: 'TcKeywordsProposalTable',
  props: {
    committee: {
      type: Object,
      required: true,
    },
    technicalCommittee: {
      type: Object,
      required: true,
    },
    baselineSnapshot: {
      type: Object,
      required: true,
    },
    similarBusy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rows: [],
      syncing: false,
      fields: [
        { key: 'tcLabel', label: 'TC', thStyle: { minWidth: '5.5rem' } },
        { key: 'existingKeywordName', label: 'Existing keyword', thStyle: { minWidth: '12rem' } },
        { key: 'tcAction', label: 'Action', thStyle: { minWidth: '8rem' } },
        { key: 'tcProposedName', label: 'Proposed by TC Chair', thStyle: { minWidth: '18rem' } },
        { key: 'similar', label: '', thStyle: { minWidth: '10rem' } },
      ],
      existingActionOptions: [
        { value: 'keep', text: 'Keep' },
        { value: 'remove', text: 'Remove' },
        { value: 'modify', text: 'Modify' },
      ],
    };
  },
  watch: {
    technicalCommittee: {
      immediate: true,
      deep: true,
      handler() {
        if (this.syncing) {
          return;
        }

        this.refreshRows();
      },
    },
  },
  methods: {
    isDraftRow(row) {
      return !row.existingKeywordId;
    },
    refreshRows() {
      this.rows = Revisions.buildTcProposalRows(
        this.baselineSnapshot,
        this.committee,
        this.technicalCommittee,
      );
    },
    onActionChange(row) {
      if (row.tcAction === 'keep' && row.existingKeywordName) {
        row.tcProposedName = row.existingKeywordName;
      }

      if (row.tcAction === 'remove') {
        row.tcProposedName = '';
      }

      this.syncRow();
    },
    onProposedNameInput(row) {
      if (row.existingKeywordId) {
        Revisions.applyTcActionFromProposedName(row);
      }

      this.syncRow();
    },
    syncRow() {
      this.syncing = true;

      try {
        Revisions.syncKeywordsFromTcRows(this.rows, this.technicalCommittee);
        this.$emit('change', this.technicalCommittee.keywords);
      } finally {
        this.$nextTick(() => {
          this.syncing = false;
        });
      }
    },
    removeDraftRow(row) {
      const index = this.rows.findIndex((candidate) => candidate.rowId === row.rowId);

      if (index === -1) {
        return;
      }

      this.rows.splice(index, 1);
      this.syncRow();
    },
    addRow() {
      const newDraftId = draftId();
      const tcLabel = this.committee?.number !== undefined
        && this.technicalCommittee?.number !== undefined
        ? `${this.committee.number}.${this.technicalCommittee.number}`
        : null;

      this.rows.push({
        rowId: newDraftId,
        tcLabel,
        existingKeywordId: null,
        existingKeywordName: null,
        tcAction: 'add',
        tcProposedName: '',
        draftId: newDraftId,
        keywordRef: { draftId: newDraftId, name: '' },
      });
      this.syncRow();
    },
  },
};
</script>
